import { useEffect, useRef, useState } from 'react';
import SelectRecherche from './SelectRecherche';
import BarreProgression, { useProgression } from './BarreProgression';
import '../assets/styles/ImportTexte.css';

/**
 * Import en trois temps : la source (le site, ou un texte collé), la
 * relecture des propositions, le bilan de l'enregistrement.
 *
 * Le composant ne sait rien du métier : `analyser` (texte) ou
 * `analyserSource` (lecture du site) renvoient des propositions
 * `{ index, retenir, anomalies, [cleObjet]: {...} }`, `colonnes` dit
 * quels champs montrer et comment les corriger, `enregistrer` reçoit les
 * propositions cochées. Sessions du calendrier et formations du catalogue
 * s'en servent avec leurs propres colonnes.
 *
 * Une proposition « bloquante » (formation inconnue, date illisible) est
 * décochée d'office : c'est à l'humain de corriger la ligne, puis de la
 * cocher. Le serveur garde ses contrôles à l'enregistrement, et le bilan
 * dit ligne par ligne ce qui n'est pas passé.
 *
 * `remplacement` : une case « supprimer d'abord toute la liste » à la
 * relecture, pour remplacer le catalogue ou le calendrier par le site.
 */

const villesVersTexte = (lieux) =>
  (lieux || [])
    .map((l) => (l.modalite === 'distanciel' && !/en ligne|distance|visio/i.test(l.ville) ? `${l.ville} (à distance)` : l.ville))
    .join(', ');

const texteVersVilles = (texte, existants = []) =>
  String(texte || '')
    .split(/[,;/]+/)
    .map((v) => v.trim())
    .filter(Boolean)
    .map((brut) => {
      const distance = /\(à distance\)|\(distanciel\)/i.test(brut);
      const ville = brut.replace(/\s*\((à distance|distanciel|présentiel|presentiel)\)\s*/i, '').trim();
      const connu = existants.find((l) => l.ville.toLowerCase() === ville.toLowerCase());
      const modalite =
        distance || /en ligne|distance|visio/i.test(ville)
          ? 'distanciel'
          : connu?.modalite || 'presentiel';
      return { ville, modalite, adresse: connu?.adresse || '' };
    });

function ImportTexteModal({
  titre,
  aide,
  exemple,
  motPluriel,
  cleObjet,
  colonnes,
  analyser,
  enregistrer,
  onFermer,
  onEnregistre,
  // Facultatif : { libelle, charger } — un bouton qui remplit le texte
  // depuis une source (la page du site), à la place du copier-coller.
  chargerTexte = null,
  // Facultatif : { libelle, placeholder, aide, charger(lien) } — un champ pour
  // coller un LIEN (formulaire Tally…) : la page est lue, son texte remplit la
  // zone, puis l'analyse part d'elle-même.
  lienSource = null,
  // Facultatif : lecture directe de la source, sans passer par le texte
  // (le site balise ses fiches : pas besoin de l'IA).
  analyserSource = null,
  // Avec `demarrerDepuisSource`, la fenêtre lit la source d'elle-même et
  // arrive directement sur la relecture.
  demarrerDepuisSource = false,
  // Facultatif : { libelle, note, supprimerTout, libelleBilan } — vider la
  // liste actuelle avant d'enregistrer.
  remplacement = null,
}) {
  const [etape, setEtape] = useState('texte');
  const [texte, setTexte] = useState('');
  const [propositions, setPropositions] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [bilan, setBilan] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [source, setSource] = useState(null);
  const [remplacer, setRemplacer] = useState(false);
  const [filtre, setFiltre] = useState('toutes'); // toutes | cochees | a-verifier
  // Lignes dépliées en édition ; les incomplètes s'ouvrent d'elles-mêmes.
  const [ouvertes, setOuvertes] = useState(() => new Set());
  const progression = useProgression(enCours || chargement, {
    estimeeMs: etape === 'texte' ? (demarrerDepuisSource ? 20000 : 20000) : 6000,
  });

  const singulier = motPluriel.replace(/s$/, '');
  const enCartes = colonnes.length >= 6;

  const manque = (objet, c) => {
    const v = objet[c.cle];
    return v === null || v === undefined || String(v).trim() === '';
  };

  /** Pose les propositions et ouvre celles qu'il faut compléter. */
  function poserPropositions(props) {
    setPropositions(props);
    setOuvertes(
      new Set(
        props
          .map((p, i) => (colonnes.some((c) => c.obligatoire && manque(p[cleObjet], c)) ? i : -1))
          .filter((i) => i >= 0)
      )
    );
    setFiltre('toutes');
  }

  function basculerOuverte(i) {
    setOuvertes((prev) => {
      const suite = new Set(prev);
      if (suite.has(i)) suite.delete(i);
      else suite.add(i);
      return suite;
    });
  }

  // Lien d'une source en ligne (formulaire Tally) : lu, puis analysé.
  const [lien, setLien] = useState('');
  const [infoLien, setInfoLien] = useState(null);

  async function lireLien() {
    if (!lien.trim() || !lienSource) return;
    setChargement(true);
    setErreur(null);
    setInfoLien(null);
    let contenu = '';
    try {
      const res = await lienSource.charger(lien.trim());
      const d = res.data || res;
      contenu = d.texte || '';
      setTexte(contenu);
      setSource(d.nom ? `${d.nom} (${d.lien || 'lien'})` : d.lien || 'le lien');
      setInfoLien(res.message || null);
    } catch (err) {
      setErreur(err.message || 'Lecture du lien impossible : collez le texte.');
      return;
    } finally {
      setChargement(false);
    }
    if (contenu.trim()) await lancerAnalyse(contenu);
  }

  async function lancerAnalyse(contenu = texte) {
    setEnCours(true);
    setErreur(null);
    try {
      const { propositions: props } = await analyser(contenu);
      poserPropositions(props);
      setEtape('relecture');
    } catch (err) {
      setErreur(err.message || 'Analyse impossible.');
    } finally {
      setEnCours(false);
    }
  }

  /** Lecture directe du site : les propositions arrivent sans texte ni IA. */
  async function lancerDepuisSource() {
    setChargement(true);
    setErreur(null);
    try {
      const res = await analyserSource();
      poserPropositions(res.propositions || []);
      setSource(res.meta?.source || (chargerTexte && chargerTexte.libelle) || 'le site');
      setEtape('relecture');
    } catch (err) {
      setErreur(
        `${err.message || 'Lecture du site impossible.'} Vous pouvez coller le texte de la page ci-dessous.`
      );
    } finally {
      setChargement(false);
    }
  }

  async function remplirDepuisSource({ puisAnalyser = false } = {}) {
    setChargement(true);
    setErreur(null);
    let contenu = '';
    try {
      const res = await chargerTexte.charger();
      const d = res.data || res;
      contenu = d.texte || '';
      setTexte(contenu);
      setSource(d.url || chargerTexte.libelle);
    } catch (err) {
      setErreur(err.message || 'Lecture du site impossible : collez le texte.');
      return;
    } finally {
      setChargement(false);
    }
    if (puisAnalyser && contenu.trim()) await lancerAnalyse(contenu);
  }

  // « Importer depuis le site » : lecture structurée si elle existe, sinon
  // texte du site puis analyse — sans clic de plus.
  const demarre = useRef(false);
  useEffect(() => {
    if (!demarrerDepuisSource || demarre.current) return;
    demarre.current = true;
    if (analyserSource) lancerDepuisSource();
    else if (chargerTexte) remplirDepuisSource({ puisAnalyser: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function majProposition(i, transformer) {
    setPropositions((prev) => prev.map((p, k) => (k === i ? transformer(p) : p)));
  }

  function majChamp(i, cle, valeur) {
    majProposition(i, (p) => ({ ...p, [cleObjet]: { ...p[cleObjet], [cle]: valeur } }));
  }

  /** Une ligne est cochable dès que ses champs obligatoires sont remplis. */
  const estComplete = (p) =>
    colonnes.every((c) => {
      if (!c.obligatoire) return true;
      const v = p[cleObjet][c.cle];
      return v !== null && v !== undefined && String(v).trim() !== '';
    });

  const retenues = propositions.filter((p) => p.retenir && estComplete(p));
  const aVerifier = propositions.filter((p) => p.anomalies.some((a) => a.gravite === 'bloquant') || !estComplete(p));

  async function lancerEnregistrement() {
    setEnCours(true);
    setErreur(null);
    try {
      let suppression = null;
      if (remplacer && remplacement) suppression = await remplacement.supprimerTout();
      const resultat = await enregistrer(retenues);
      const complet = { ...resultat, suppression };
      setBilan(complet);
      setEtape('bilan');
      onEnregistre?.(complet);
    } catch (err) {
      setErreur(err.message || 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  }

  function toutCocher(valeur) {
    setPropositions((prev) => prev.map((p) => ({ ...p, retenir: valeur && estComplete(p) })));
  }

  function rendreChamp(p, i, c) {
    const objet = p[cleObjet];
    const valeur = objet[c.cle];
    const commun = { disabled: enCours };

    if (c.type === 'select') {
      return (
        <SelectRecherche
          value={valeur ?? ''}
          onChange={(e) => majChamp(i, c.cle, e.target.value === '' ? null : c.numerique ? Number(e.target.value) : e.target.value)}
          className="imp-select"
          {...commun}
        >
          <option value="">{c.vide || '—'}</option>
          {c.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </SelectRecherche>
      );
    }
    if (c.type === 'checkbox') {
      return (
        <input
          type="checkbox"
          checked={Boolean(valeur)}
          onChange={(e) => majChamp(i, c.cle, e.target.checked)}
          {...commun}
        />
      );
    }
    if (c.type === 'lieux') {
      return (
        <input
          type="text"
          className="imp-input"
          value={villesVersTexte(valeur)}
          placeholder="Paris, En ligne"
          onChange={(e) => majChamp(i, c.cle, texteVersVilles(e.target.value, valeur))}
          {...commun}
        />
      );
    }
    if (c.type === 'textarea') {
      return (
        <textarea
          className="imp-input imp-textarea"
          rows={2}
          value={valeur ?? ''}
          onChange={(e) => majChamp(i, c.cle, e.target.value || null)}
          {...commun}
        />
      );
    }
    return (
      <input
        type={c.type || 'text'}
        step={c.type === 'number' ? c.pas || 'any' : undefined}
        min={c.type === 'number' ? c.min : undefined}
        className="imp-input"
        value={valeur ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          majChamp(i, c.cle, v === '' ? null : c.type === 'number' ? Number(v) : v);
        }}
        {...commun}
      />
    );
  }

  /** Valeur d'un champ telle qu'on la lit dans le résumé d'une ligne. */
  function valeurLisible(objet, c) {
    const v = objet[c.cle];
    if (v === null || v === undefined || v === '') return null;
    if (c.type === 'select') {
      const o = c.options.find((x) => String(x.value) === String(v));
      return o ? o.label : String(v);
    }
    if (c.type === 'checkbox') return v ? c.label : null;
    if (c.type === 'lieux') return villesVersTexte(v) || null;
    if (c.type === 'date') {
      const d = new Date(`${v}T00:00:00`);
      return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('fr-FR');
    }
    return String(v);
  }

  /**
   * Une proposition en ligne : case, résumé lisible, état, bouton Modifier
   * qui déplie le formulaire complet (titre, grille, textes).
   */
  function rendreLigne(p, i) {
    const objet = p[cleObjet];
    const complete = estComplete(p);
    const bloquee = p.anomalies.some((a) => a.gravite === 'bloquant');
    const avertie = p.anomalies.some((a) => a.gravite !== 'bloquant');
    const ouverte = ouvertes.has(i);
    const titres = colonnes.filter((c) => c.zone === 'titre');
    const larges = colonnes.filter((c) => c.zone !== 'titre' && (c.zone === 'large' || c.type === 'textarea'));
    const grille = colonnes.filter((c) => !titres.includes(c) && !larges.includes(c));
    const marquees = colonnes.filter((c) => c.resume && !titres.includes(c));
    const details = marquees.length ? marquees : grille.slice(0, 5);
    const principal = titres.find((c) => c.grand) || titres[0] || colonnes[0];
    const secondaires = titres.filter((c) => c !== principal);
    const classe =
      'imp-ligne' +
      (p.retenir && complete ? ' imp-ligne-retenue' : '') +
      (!complete || bloquee ? ' imp-ligne-a-verifier' : '') +
      (ouverte ? ' imp-ligne-ouverte' : '');

    return (
      <div className={classe} key={p.index ?? i}>
        <div className="imp-ligne-tete">
          <label
            className="imp-ligne-coche"
            title={complete ? 'Enregistrer cette ligne' : 'Complétez les champs marqués * pour pouvoir cocher'}
          >
            <input
              type="checkbox"
              checked={Boolean(p.retenir && complete)}
              disabled={!complete || enCours}
              onChange={(e) => majProposition(i, (q) => ({ ...q, retenir: e.target.checked }))}
            />
          </label>
          <span className="imp-ligne-numero">{i + 1}</span>
          <button type="button" className="imp-ligne-resume" onClick={() => basculerOuverte(i)}>
            <span className="imp-ligne-principal">
              {secondaires.map((c) => (
                <span key={c.cle} className="imp-ligne-code">
                  {valeurLisible(objet, c) ?? <em className="imp-manquant">{c.label} manquant</em>}
                </span>
              ))}
              <span className="imp-ligne-nom">
                {valeurLisible(objet, principal) ?? <em className="imp-manquant">{principal.label} manquant</em>}
              </span>
            </span>
            <span className="imp-ligne-details">
              {details.map((c) => {
                const v = valeurLisible(objet, c);
                if (v === null && !c.obligatoire) return null;
                return (
                  <span key={c.cle} className="imp-puce">
                    <span className="imp-puce-libelle">{c.label}</span>
                    {v === null ? <em className="imp-manquant">manquant</em> : v}
                  </span>
                );
              })}
            </span>
          </button>
          <span className="imp-ligne-etat">
            {'existantId' in p &&
              (p.existantId ? (
                <span className="imp-pastille imp-pastille-maj">Mise à jour</span>
              ) : (
                <span className="imp-pastille imp-pastille-neuve">Nouvelle</span>
              ))}
            {bloquee ? (
              <span className="imp-pastille imp-pastille-bloquant">Bloquée</span>
            ) : !complete ? (
              <span className="imp-pastille imp-pastille-alerte">À compléter</span>
            ) : avertie ? (
              <span className="imp-pastille imp-pastille-alerte">À vérifier</span>
            ) : null}
          </span>
          <button
            type="button"
            className={`imp-ligne-modifier${ouverte ? ' imp-ligne-modifier-ouvert' : ''}`}
            onClick={() => basculerOuverte(i)}
            aria-expanded={ouverte}
            disabled={enCours}
          >
            {ouverte ? 'Fermer' : 'Modifier'}
          </button>
        </div>

        {p.anomalies.length > 0 && (
          <div className="imp-ligne-anomalies">
            {p.anomalies.map((a, k) => (
              <span key={k} className={`imp-anomalie imp-anomalie-${a.gravite}`}>
                {a.message}
              </span>
            ))}
          </div>
        )}

        {ouverte && (
          <div className="imp-ligne-edition">
            <div className="imp-carte-titre">
              {titres.map((c) => (
                <label key={c.cle} className={`imp-champ${c.grand ? ' imp-champ-grand' : ''}`}>
                  <span className="imp-champ-libelle">
                    {c.label}
                    {c.obligatoire ? ' *' : ''}
                  </span>
                  {rendreChamp(p, i, c)}
                </label>
              ))}
            </div>
            <div className="imp-carte-grille">
              {grille.map((c) => (
                <label key={c.cle} className={`imp-champ${c.type === 'checkbox' ? ' imp-champ-case' : ''}`}>
                  <span className="imp-champ-libelle">
                    {c.label}
                    {c.obligatoire ? ' *' : ''}
                  </span>
                  {rendreChamp(p, i, c)}
                </label>
              ))}
            </div>
            {larges.map((c) => (
              <label key={c.cle} className="imp-champ imp-champ-large">
                <span className="imp-champ-libelle">{c.label}</span>
                {rendreChamp(p, i, c)}
              </label>
            ))}
          </div>
        )}
      </div>
    );
  }

  const visibles = propositions
    .map((p, i) => ({ p, i }))
    .filter(({ p }) => {
      if (filtre === 'cochees') return p.retenir && estComplete(p);
      if (filtre === 'a-verifier') return aVerifier.includes(p);
      return true;
    });

  return (
    <div className="imp-overlay" onClick={onFermer}>
      <div className="imp-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={titre}>
        <div className="imp-header">
          <div>
            <h2 className="imp-titre">{titre}</h2>
            <div className="imp-etapes">
              <span className={etape === 'texte' ? 'imp-etape-active' : ''}>
                1. {analyserSource ? 'Lire le site' : 'Coller le texte'}
              </span>
              <span className={etape === 'relecture' ? 'imp-etape-active' : ''}>2. Relire</span>
              <span className={etape === 'bilan' ? 'imp-etape-active' : ''}>3. Bilan</span>
            </div>
          </div>
          <button type="button" className="imp-fermer" onClick={onFermer} aria-label="Fermer">
            ×
          </button>
        </div>

        <div className="imp-corps">
          {etape === 'texte' && (
            <>
              {chargement && analyserSource ? (
                <p className="imp-aide">Lecture du site en cours…</p>
              ) : (
                <>
                  <p className="imp-aide">{aide}</p>
                  {lienSource && (
                    <>
                      <div className="imp-lien">
                        <input
                          type="url"
                          className="imp-lien-champ"
                          value={lien}
                          onChange={(e) => setLien(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              lireLien();
                            }
                          }}
                          placeholder={lienSource.placeholder}
                          disabled={enCours || chargement}
                          aria-label="Lien à importer"
                        />
                        <button
                          type="button"
                          className="imp-btn imp-btn-primary"
                          onClick={lireLien}
                          disabled={enCours || chargement || !lien.trim()}
                        >
                          {chargement ? 'Lecture…' : enCours ? 'Analyse…' : lienSource.libelle}
                        </button>
                      </div>
                      {lienSource.aide && <p className="imp-aide imp-aide-discrete">{lienSource.aide}</p>}
                      {infoLien && <p className="imp-aide">{infoLien}</p>}
                    </>
                  )}
                  <textarea
                    className="imp-zone"
                    value={texte}
                    onChange={(e) => setTexte(e.target.value)}
                    placeholder={exemple}
                    disabled={enCours}
                    autoFocus={!demarrerDepuisSource}
                  />
                  <div className="imp-compteur">
                    {texte.length.toLocaleString('fr-FR')} caractères
                    {source ? ` · lu sur ${source}` : ''}
                  </div>
                  {(analyserSource || chargerTexte) && (
                    <div className="imp-source">
                      {analyserSource && (
                        <button
                          type="button"
                          className="imp-btn imp-btn-primary"
                          onClick={lancerDepuisSource}
                          disabled={enCours || chargement}
                          title="Lit les fiches du site telles quelles et passe à la relecture"
                        >
                          Lire le site
                        </button>
                      )}
                      {chargerTexte && (
                        <button
                          type="button"
                          className="imp-btn"
                          onClick={() => remplirDepuisSource()}
                          disabled={enCours || chargement}
                          title="Met le texte de la page ici, à analyser par l’IA"
                        >
                          {chargement ? 'Lecture du site…' : chargerTexte.libelle}
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {etape === 'relecture' && (
            <>
              {propositions.length === 0 ? (
                <p className="imp-aide">Rien d’exploitable : complétez le texte ou saisissez à la main.</p>
              ) : (
                <>
                  <div className="imp-relecture-tete">
                    <div className="imp-stats">
                      <button
                        type="button"
                        className={`imp-stat imp-stat-bouton${filtre === 'toutes' ? ' imp-stat-active' : ''}`}
                        onClick={() => setFiltre('toutes')}
                      >
                        <span className="imp-stat-nombre">{propositions.length}</span>
                        <span className="imp-stat-libelle">trouvée{propositions.length > 1 ? 's' : ''}</span>
                      </button>
                      <button
                        type="button"
                        className={`imp-stat imp-stat-ok imp-stat-bouton${filtre === 'cochees' ? ' imp-stat-active' : ''}`}
                        onClick={() => setFiltre('cochees')}
                      >
                        <span className="imp-stat-nombre">{retenues.length}</span>
                        <span className="imp-stat-libelle">à enregistrer</span>
                      </button>
                      {aVerifier.length > 0 && (
                        <button
                          type="button"
                          className={`imp-stat imp-stat-alerte imp-stat-bouton${filtre === 'a-verifier' ? ' imp-stat-active' : ''}`}
                          onClick={() => setFiltre('a-verifier')}
                        >
                          <span className="imp-stat-nombre">{aVerifier.length}</span>
                          <span className="imp-stat-libelle">à vérifier</span>
                        </button>
                      )}
                      {source && <span className="imp-relecture-note">Source : {source}</span>}
                    </div>
                    <span className="imp-relecture-actions">
                      <button type="button" className="imp-lien" onClick={() => toutCocher(true)}>
                        Tout cocher
                      </button>
                      <button type="button" className="imp-lien" onClick={() => toutCocher(false)}>
                        Tout décocher
                      </button>
                      {enCartes && (
                        <button
                          type="button"
                          className="imp-lien"
                          onClick={() =>
                            setOuvertes(
                              ouvertes.size >= visibles.length
                                ? new Set()
                                : new Set(propositions.map((_, k) => k))
                            )
                          }
                        >
                          {ouvertes.size >= visibles.length ? 'Tout replier' : 'Tout déplier'}
                        </button>
                      )}
                    </span>
                  </div>

                  {remplacement && (
                    <label className="imp-remplacement">
                      <input
                        type="checkbox"
                        checked={remplacer}
                        onChange={(e) => setRemplacer(e.target.checked)}
                        disabled={enCours}
                      />
                      <span>
                        <strong>{remplacement.libelle}</strong>
                        {remplacement.note ? <em> — {remplacement.note}</em> : null}
                      </span>
                    </label>
                  )}

                  {enCartes ? (
                    <div className="imp-lignes">
                      {visibles.length === 0 && (
                        <p className="imp-aide">Aucune ligne dans ce filtre.</p>
                      )}
                      {visibles.map(({ p, i }) => rendreLigne(p, i))}
                    </div>
                  ) : (
                    <div className="imp-tableau-wrap">
                      <table className="imp-tableau">
                        <thead>
                          <tr>
                            <th className="imp-col-coche" />
                            {colonnes.map((c) => (
                              <th key={c.cle} style={c.largeur ? { minWidth: c.largeur } : undefined}>
                                {c.label}
                                {c.obligatoire ? ' *' : ''}
                              </th>
                            ))}
                            <th className="imp-col-anomalies">À vérifier</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibles.map(({ p, i }) => {
                            const complete = estComplete(p);
                            return (
                              <tr key={p.index ?? i} className={p.retenir && complete ? 'imp-ligne-retenue' : ''}>
                                <td className="imp-col-coche">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(p.retenir && complete)}
                                    disabled={!complete || enCours}
                                    title={complete ? '' : 'Complétez les champs marqués * pour pouvoir cocher'}
                                    onChange={(e) => majProposition(i, (q) => ({ ...q, retenir: e.target.checked }))}
                                  />
                                </td>
                                {colonnes.map((c) => (
                                  <td key={c.cle}>{rendreChamp(p, i, c)}</td>
                                ))}
                                <td className="imp-col-anomalies">
                                  {p.anomalies.map((a, k) => (
                                    <div key={k} className={`imp-anomalie imp-anomalie-${a.gravite}`}>
                                      {a.message}
                                    </div>
                                  ))}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {etape === 'bilan' && bilan && (
            <div className="imp-bilan">
              <div className={`imp-bilan-tete${bilan.erreurs?.length ? ' imp-bilan-tete-alerte' : ''}`}>
                <span className="imp-bilan-icone" aria-hidden="true">
                  {bilan.erreurs?.length ? '!' : '✓'}
                </span>
                <div>
                  <div className="imp-bilan-titre">
                    {bilan.erreurs?.length ? 'Import terminé, avec des réserves' : 'Import terminé'}
                  </div>
                  <div className="imp-bilan-sous">
                    {bilan.total} {bilan.total > 1 ? motPluriel : singulier} traitée{bilan.total > 1 ? 's' : ''}
                    {source ? ` · source : ${source}` : ''}
                  </div>
                </div>
              </div>
              <div className="imp-stats imp-stats-bilan">
                {bilan.suppression && remplacement?.libelleBilan && (
                  <div className="imp-stat imp-stat-alerte">
                    <span className="imp-stat-libelle">Liste précédente : {remplacement.libelleBilan(bilan.suppression)}</span>
                  </div>
                )}
                <div className="imp-stat imp-stat-ok">
                  <span className="imp-stat-nombre">{bilan.creees ?? 0}</span>
                  <span className="imp-stat-libelle">créée{(bilan.creees ?? 0) > 1 ? 's' : ''}</span>
                </div>
                {bilan.misesAJour !== undefined && (
                  <div className="imp-stat">
                    <span className="imp-stat-nombre">{bilan.misesAJour}</span>
                    <span className="imp-stat-libelle">mise{bilan.misesAJour > 1 ? 's' : ''} à jour</span>
                  </div>
                )}
                {bilan.erreurs?.length > 0 && (
                  <div className="imp-stat imp-stat-alerte">
                    <span className="imp-stat-nombre">{bilan.erreurs.length}</span>
                    <span className="imp-stat-libelle">non enregistrée{bilan.erreurs.length > 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>
              {bilan.erreurs?.length > 0 && (
                <ul className="imp-bilan-erreurs">
                  {bilan.erreurs.map((e) => (
                    <li key={`${e.index}-${e.message}`}>
                      <strong>{e.intitule || e.code || `Ligne ${e.index + 1}`}</strong> — {e.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <BarreProgression
            valeur={progression}
            libelle={
              chargement
                ? 'Lecture du site'
                : etape === 'texte'
                  ? 'Analyse du texte par l’IA'
                  : remplacer
                    ? 'Suppression de la liste, puis enregistrement'
                    : 'Enregistrement'
            }
            estimation={etape === 'texte' && !analyserSource}
          />
          {erreur && <div className="imp-erreur">{erreur}</div>}
        </div>

        <div className="imp-pied">
          {etape === 'texte' && (
            <>
              <button type="button" className="imp-btn" onClick={onFermer} disabled={enCours}>
                Annuler
              </button>
              <button
                type="button"
                className="imp-btn imp-btn-primary"
                onClick={() => lancerAnalyse()}
                disabled={enCours || chargement || !texte.trim()}
              >
                {enCours ? 'Analyse en cours…' : 'Analyser le texte'}
              </button>
            </>
          )}
          {etape === 'relecture' && (
            <>
              <button type="button" className="imp-btn" onClick={() => setEtape('texte')} disabled={enCours}>
                ← Revenir
              </button>
              <button
                type="button"
                className={`imp-btn ${remplacer ? 'imp-btn-danger' : 'imp-btn-primary'}`}
                onClick={lancerEnregistrement}
                disabled={enCours || retenues.length === 0}
              >
                {enCours
                  ? remplacer
                    ? 'Remplacement…'
                    : 'Enregistrement…'
                  : `${remplacer ? 'Remplacer la liste par' : 'Enregistrer'} ${retenues.length} ${retenues.length > 1 ? motPluriel : singulier}`}
              </button>
            </>
          )}
          {etape === 'bilan' && (
            <button type="button" className="imp-btn imp-btn-primary" onClick={onFermer}>
              Fermer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImportTexteModal;
